import type {
  CustomerOrderSummary,
  CustomerStatus,
  CustomerStatusCode,
  OrderPublicStatus,
  OrderShort,
} from '~/api/Order';

/**
 * Stages of the customer journey as drawn on the route card, in order. The
 * fifth stage is the last mile: `OUT_FOR_DELIVERY` for courier orders and
 * `READY_FOR_PICKUP` for pickup-point ones.
 */
export const CUSTOMER_STATUS_STAGES: readonly CustomerStatusCode[] = [
  'ORDER_CONFIRMED',
  'PREPARING',
  'READY_FOR_SHIPMENT',
  'ON_THE_WAY',
  'OUT_FOR_DELIVERY',
  'DELIVERED',
];

const LAST_MILE_STAGE = CUSTOMER_STATUS_STAGES.indexOf('OUT_FOR_DELIVERY');

/** Customer statuses derived from the legacy `public_status` when the backend sends no `customer_status`. */
const LEGACY_CUSTOMER_STATUS: Record<OrderPublicStatus, CustomerStatusCode> = {
  accepted: 'ORDER_CONFIRMED',
  picking: 'PREPARING',
  ready_to_ship: 'READY_FOR_SHIPMENT',
  in_delivery: 'ON_THE_WAY',
  ready_for_pickup: 'READY_FOR_PICKUP',
  delivered: 'DELIVERED',
  failed: 'CANCELLED',
  cancelled: 'CANCELLED',
  not_collected: 'CANCELLED',
};

const FINAL_CUSTOMER_STATUSES: readonly CustomerStatusCode[] = [
  'DELIVERED',
  'CANCELLED',
];

export type OrderStatusSource = Pick<
  OrderShort,
  'customer_status' | 'public_status' | 'updated_at'
>;

/**
 * Customer-facing status of a shipment. Older backends send no
 * `customer_status`, so the legacy `public_status` is mapped onto the same
 * vocabulary without a comment.
 */
export const resolveCustomerStatus = (
  order: OrderStatusSource,
): CustomerStatus =>
  order.customer_status ?? {
    code: LEGACY_CUSTOMER_STATUS[order.public_status] ?? 'ORDER_CONFIRMED',
    label: '',
    comment_key: null,
    comment: null,
    comment_params: null,
    action_required: false,
    updated_at: order.updated_at,
    flow: null,
    source: 'legacy',
  };

/** Index of the route stage a status sits at; cancelled orders have no stage. */
export const getCustomerStatusStage = (
  code: CustomerStatusCode,
): number | null => {
  switch (code) {
    case 'AWAITING_PASSPORT':
      return 0;
    case 'READY_FOR_PICKUP':
      return LAST_MILE_STAGE;
    case 'CANCELLED':
      return null;
    default:
      return CUSTOMER_STATUS_STAGES.indexOf(code);
  }
};

export const isFinalCustomerStatus = (code: CustomerStatusCode): boolean =>
  FINAL_CUSTOMER_STATUSES.includes(code);

/** Colour of the status chip: pickup-ready orders are the only highlighted ones in the mockup. */
export const getCustomerStatusTone = (
  code: CustomerStatusCode,
): 'pickup' | 'neutral' => (code === 'READY_FOR_PICKUP' ? 'pickup' : 'neutral');

/** Stage code to label the last-mile stage with, by delivery destination. */
export const getStageCode = (
  stage: number,
  isPickup: boolean,
): CustomerStatusCode => {
  if (stage === LAST_MILE_STAGE && isPickup) {
    return 'READY_FOR_PICKUP';
  }
  return CUSTOMER_STATUS_STAGES[stage] ?? 'ORDER_CONFIRMED';
};

/** Shipments of one customer order as listed on the orders page. */
export interface CustomerOrderGroup {
  /** Stable key for React lists. */
  key: string;
  /** Number the customer knows the order by. */
  orderNumber: string;
  /** Shipment that opens the order page. */
  primary: OrderShort;
  /** Shipments of the order present in the list, in list order. */
  parts: OrderShort[];
  /** Shipments the backend counts, including ones outside the loaded pages. */
  shipmentsTotal: number;
  summary: CustomerOrderSummary | null;
}

const getCustomerOrderKey = (order: OrderShort): string | null =>
  order.multi_order?.id ?? order.multi_order_id ?? null;

/**
 * Collapses the flat shipment list of the orders endpoint into customer
 * orders: shipments split from the same order share a `multi_order`. Shipments
 * keep the order they arrived in; a group sits where its first shipment was.
 */
export const groupOrdersByCustomerOrder = (
  orders: OrderShort[],
): CustomerOrderGroup[] => {
  const groups: CustomerOrderGroup[] = [];
  const byKey = new Map<string, CustomerOrderGroup>();

  for (const order of orders) {
    const key = getCustomerOrderKey(order);
    const existing = key ? byKey.get(key) : undefined;

    if (existing) {
      existing.parts.push(order);
      existing.shipmentsTotal = Math.max(
        existing.shipmentsTotal,
        existing.parts.length,
      );
      continue;
    }

    const summary = order.customer_order_summary ?? null;
    const group: CustomerOrderGroup = {
      key: key ?? order.id,
      orderNumber: order.multi_order?.order_id ?? order.order_id,
      primary: order,
      parts: [order],
      shipmentsTotal: Math.max(1, summary?.shipments_total ?? 1),
      summary,
    };

    groups.push(group);
    if (key) {
      byKey.set(key, group);
    }
  }

  return groups;
};

export const isSplitCustomerOrder = (group: CustomerOrderGroup): boolean =>
  group.shipmentsTotal > 1;

/**
 * Numbers of the shipments the summary lists but the loaded page does not
 * hold. The orders endpoint filters each tab by OMS status, so a delivered
 * shipment of a split order only comes with the history tab and vice versa.
 */
export const getMissingShipmentNumbers = (
  group: CustomerOrderGroup,
): string[] => {
  const loaded = new Set(group.parts.map((part) => part.order_id));
  return (group.summary?.shipments ?? [])
    .map((shipment) => shipment.order_id)
    .filter((orderId) => !loaded.has(orderId));
};

/**
 * Completes a customer order with shipments loaded outside the list and puts
 * them in the order the summary lists them. The customer order number is only
 * known from a detailed shipment, so it is taken from any part that has it.
 */
export const withLoadedShipments = (
  group: CustomerOrderGroup,
  loaded: Record<string, OrderShort>,
): CustomerOrderGroup => {
  const numbers = (group.summary?.shipments ?? []).map(
    (shipment) => shipment.order_id,
  );
  const byNumber = new Map(group.parts.map((part) => [part.order_id, part]));
  for (const orderId of numbers) {
    const extra = loaded[orderId];
    if (extra && !byNumber.has(orderId)) {
      byNumber.set(orderId, extra);
    }
  }
  const position = (part: OrderShort) => {
    const index = numbers.indexOf(part.order_id);
    return index >= 0 ? index : numbers.length;
  };
  const parts = [...byNumber.values()].sort(
    (a, b) => position(a) - position(b),
  );
  const detailed = [
    ...parts,
    ...numbers.map((orderId) => loaded[orderId]),
  ].find((part) => part?.multi_order?.order_id);

  return {
    ...group,
    parts,
    orderNumber: detailed?.multi_order?.order_id ?? group.orderNumber,
  };
};

/** A customer order belongs to the history once every shipment of it is delivered or cancelled. */
export const isCustomerOrderFinished = (group: CustomerOrderGroup): boolean => {
  const codes = group.summary?.shipments.length
    ? group.summary.shipments.map((shipment) => shipment.code)
    : group.parts.map((part) => resolveCustomerStatus(part).code);
  return codes.length > 0 && codes.every(isFinalCustomerStatus);
};

/**
 * Shipments the customer pays for: cancelled ones are refunded, so they stay
 * out of the totals unless the whole customer order is cancelled.
 */
export const getChargedShipments = (parts: OrderShort[]): OrderShort[] => {
  const charged = parts.filter(
    (part) => resolveCustomerStatus(part).code !== 'CANCELLED',
  );
  return charged.length > 0 ? charged : parts;
};

/** A status or comment on the route of a shipment. */
export interface RouteEvent {
  code: CustomerStatusCode;
  label: string;
  commentKey: string | null;
  comment: string | null;
  /** ISO datetime of the event. */
  at: string;
}

/**
 * Events of a shipment in time order: its history and, unless the history
 * already ends with it, the current comment stamped with its own time. The
 * backend collapses repeats of one text (`passport.requested` followed by
 * `gbs.NW`) into the first event, so a same-text current comment is a repeat.
 */
export const getRouteEvents = (status: CustomerStatus): RouteEvent[] => {
  const events: RouteEvent[] = [...(status.history ?? [])]
    .sort((a, b) => a.occurred_at.localeCompare(b.occurred_at))
    .map((entry) => ({
      code: entry.code,
      label: entry.label,
      commentKey: entry.comment_key,
      comment: entry.comment,
      at: entry.occurred_at,
    }));
  const current: RouteEvent = {
    code: status.code,
    label: status.label,
    commentKey: status.comment_key,
    comment: status.comment,
    at: status.comment_updated_at ?? status.updated_at,
  };
  const last = events[events.length - 1];
  const repeated =
    last !== undefined &&
    last.code === current.code &&
    (last.commentKey === current.commentKey ||
      (last.comment !== null && last.comment === current.comment));
  return repeated ? events : [...events, current];
};

/** 0-based position of a shipment in its customer order, by the summary. */
export const getShipmentPosition = (
  group: CustomerOrderGroup,
  part: OrderShort,
  fallback: number,
): number => {
  const index =
    group.summary?.shipments.findIndex(
      (shipment) => shipment.order_id === part.order_id,
    ) ?? -1;
  return index >= 0 ? index : fallback;
};

/** A shipment of a customer order delivered in several shipments. */
export const isSplitOrder = (
  order: Pick<OrderShort, 'delivery_group' | 'customer_order_summary'>,
): boolean =>
  (order.delivery_group?.orders?.length ?? 0) > 1 ||
  (order.customer_order_summary?.shipments_total ?? 1) > 1;

/**
 * Number the customer knows the order by: the customer order's for a
 * shipment of a split order, the shipment's own otherwise.
 */
export const getDisplayOrderNumber = (
  order: Pick<
    OrderShort,
    'order_id' | 'multi_order' | 'delivery_group' | 'customer_order_summary'
  >,
): string =>
  isSplitOrder(order)
    ? (order.multi_order?.order_id ?? order.order_id)
    : order.order_id;

/** Translation key of a status name. */
export const customerStatusLabelKey = (code: CustomerStatusCode): string =>
  `customerStatus:codes.${code}`;

/**
 * Translation key of a status comment. Only the first dot of the backend key
 * nests (the flow: gbs, ds, tswms); later dots turn into `_`, because i18next
 * reads every dot as nesting and `gbs.IDOK.ds` would otherwise look inside the
 * string `gbs.IDOK`.
 */
export const customerStatusCommentKey = (commentKey: string): string => {
  const [flow, ...rest] = commentKey.split('.');

  return `customerStatus:comments.${[flow, rest.join('_')].filter(Boolean).join('.')}`;
};

/** Translation key of the headline of a multi-shipment order. */
export const customerOrderSummaryKey = (
  summary: CustomerOrderSummary,
): string => `customerStatus:summary.${summary.kind}`;
